import { useTranslation } from "react-i18next"
import { Clock } from "@phosphor-icons/react"
import { cn } from "@/lib/utils"
import { DAYS_ORDER, DaySchedule, getScheduleSummary } from "@/modules/care-provider/utils/get-schedule-summary"

interface ScheduleDisplayProps {
  detailedSchedule: ReadonlyArray<DaySchedule>
  isExpanded?: boolean
  showAllDays?: boolean
  className?: string
}

const ScheduleDisplay: React.FC<ScheduleDisplayProps> = ({ detailedSchedule, isExpanded, showAllDays, className }) => {
  const { t } = useTranslation()

  if (!showAllDays) {
    const allOpen24 = detailedSchedule.every((s) => s.workingType === "OPEN24_HOURS")
    const allClosed = detailedSchedule.every((s) => s.workingType === "CLOSED")
    const showSummary = !isExpanded || allOpen24 || allClosed

    if (showSummary) {
      const summary = getScheduleSummary(detailedSchedule, t)

      return (
        <div className={cn("text-on-surface flex items-center px-3 text-sm leading-tight", className)}>
          <div className="text-on-surface-subdued shrink-0 pr-2">
            <Clock />
          </div>
          <span>{summary}</span>
        </div>
      )
    }
  }

  return (
    <div className={cn("text-on-surface flex items-start px-3 text-sm leading-tight", className)}>
      {!showAllDays && (
        <div className="text-on-surface-subdued -mt-0.5 shrink-0 pr-2">
          <Clock />
        </div>
      )}
      <div className="grid gap-4">
        {DAYS_ORDER.map((day) => {
          const schedule = detailedSchedule.find((s) => s.day === day)
          const isClosed = schedule?.workingType === "CLOSED"
          const isOpen24 = schedule?.workingType === "OPEN24_HOURS"
          return (
            <div key={day} className="flex items-center gap-5">
              <span className="w-10 shrink-0">{t(`careProvider.openingHours.days.${day}`)}</span>
              {isClosed && <span>{t("careProvider.openingHours.closed")}</span>}
              {isOpen24 && <span>{t("careProvider.openingHours.open24Hours")}</span>}
              {schedule?.workingType === "HOURLY" &&
                schedule.periods?.map((period) => (
                  <span key={`${period.start}-${period.end}`}>
                    {period.start}–{period.end}
                  </span>
                ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export { ScheduleDisplay }
